import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Pencil, Trash2, Search, X, FileText } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useConfirm, Card, Button, Input, Modal, Tooltip, PhoneInput } from './ui';
import ClienteTipoToggle, { empresaEfectiva, TIPO_EMPRESA, TIPO_PERSONA } from './ClienteTipoToggle';

// Clientes creados antes de la columna `tipo`: sin empresa propia son persona natural
const inferirTipo = (cliente) => {
    if (cliente?.tipo === TIPO_PERSONA || cliente?.tipo === TIPO_EMPRESA) return cliente.tipo;
    const empresa = (cliente?.empresa || '').trim();
    return empresa && empresa.toLowerCase() !== (cliente?.nombre || '').trim().toLowerCase()
        ? TIPO_EMPRESA
        : TIPO_PERSONA;
};

const PAGE_SIZE = 10;

const ClienteDetalle = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { tienePermiso } = useAuth();
    const confirm = useConfirm();
    const puedeEditar = tienePermiso('gestion_clientes');

    // Datos del cliente
    const [cliente, setCliente] = useState(null);
    const [clienteForm, setClienteForm] = useState({
        nombre: '', tipo: TIPO_EMPRESA, empresa: '', email: '', telefono: '', notas: ''
    });
    const [loadingCliente, setLoadingCliente] = useState(true);
    const [savingCliente, setSavingCliente] = useState(false);

    // Contactos
    const [contactos, setContactos] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [busqueda, setBusqueda] = useState('');
    const [busquedaDebounced, setBusquedaDebounced] = useState('');
    const [loadingContactos, setLoadingContactos] = useState(true);

    // Modal contacto (crear / editar)
    const [contactoModal, setContactoModal] = useState(null); // null = cerrado | { ...data } = editando
    const [savingContacto, setSavingContacto] = useState(false);

    // Debounce de la busqueda (300ms)
    useEffect(() => {
        const t = setTimeout(() => setBusquedaDebounced(busqueda.trim()), 300);
        return () => clearTimeout(t);
    }, [busqueda]);

    // Cargar datos del cliente al montar
    useEffect(() => {
        const cargarCliente = async () => {
            setLoadingCliente(true);
            try {
                const res = await api.clientes.getById(id);
                const c = res.data || res;
                setCliente(c);
                const tipo = inferirTipo(c);
                setClienteForm({
                    nombre: c.nombre || '',
                    tipo,
                    // En persona natural la empresa es copia del nombre: arrancamos
                    // vacio para que al pasar a Empresa se escriba la razon social real
                    empresa: tipo === TIPO_PERSONA ? '' : (c.empresa || ''),
                    email: c.email || '',
                    telefono: c.telefono || '',
                    notas: c.notas || ''
                });
            } catch (err) {
                const status = err?.status;
                const msg = err?.message || '';
                if (status === 404 || msg.toLowerCase().includes('no encontrada') || msg.toLowerCase().includes('not found')) {
                    navigate('/404', { replace: true });
                } else {
                    toast.error('Error al cargar cliente');
                }
            } finally {
                setLoadingCliente(false);
            }
        };
        cargarCliente();
    }, [id, navigate]);

    const cargarContactos = useCallback(async (nuevoOffset = 0) => {
        setLoadingContactos(true);
        try {
            const res = await api.clientes.getContactos(id, {
                q: busquedaDebounced || undefined,
                limit: PAGE_SIZE,
                offset: nuevoOffset
            });
            setContactos(res.data || []);
            setTotal(res.pagination?.total || 0);
            setOffset(nuevoOffset);
        } catch (err) {
            toast.error('Error al cargar contactos');
        } finally {
            setLoadingContactos(false);
        }
    }, [id, busquedaDebounced]);

    // Recargar contactos cuando cambia búsqueda → reset paginación
    useEffect(() => {
        cargarContactos(0);
    }, [cargarContactos]);

    const esPersona = clienteForm.tipo === TIPO_PERSONA;

    const setClienteCampo = (campo, valor) => {
        setClienteForm(prev => ({ ...prev, [campo]: valor }));
    };

    const guardarCliente = async () => {
        if (!clienteForm.nombre.trim()) {
            toast.error('El nombre del cliente es obligatorio');
            return;
        }
        if (!esPersona && !clienteForm.empresa.trim()) {
            toast.error('La empresa es obligatoria');
            return;
        }
        setSavingCliente(true);
        try {
            const payload = { ...clienteForm, empresa: empresaEfectiva(clienteForm) };
            await api.clientes.update(id, payload);
            setCliente(prev => ({ ...prev, ...payload }));
            toast.success('Cliente actualizado');
        } catch (err) {
            toast.error('Error al guardar cliente');
        } finally {
            setSavingCliente(false);
        }
    };

    const abrirNuevoContacto = () => {
        setContactoModal({ nombre: '', telefono: '', email: '', cargo: '' });
    };

    const abrirEditarContacto = (c) => {
        setContactoModal({ ...c });
    };

    const cerrarContactoModal = () => setContactoModal(null);

    const guardarContacto = async () => {
        if (!contactoModal.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        setSavingContacto(true);
        try {
            const payload = {
                nombre: contactoModal.nombre.trim(),
                telefono: contactoModal.telefono || null,
                email: contactoModal.email || null,
                cargo: contactoModal.cargo || null
            };
            if (contactoModal.id) {
                await api.clientes.updateContacto(id, contactoModal.id, payload);
                toast.success('Contacto actualizado');
            } else {
                await api.clientes.createContacto(id, payload);
                toast.success('Contacto agregado');
            }
            cerrarContactoModal();
            cargarContactos(offset);
        } catch (err) {
            toast.error('Error al guardar contacto');
        } finally {
            setSavingContacto(false);
        }
    };

    const eliminarContacto = async (c) => {
        const ok = await confirm({
            title: '¿Eliminar contacto?',
            message: <p>Vas a eliminar a <strong>{c.nombre}</strong>. Esta acción no se puede deshacer.</p>,
            confirmText: 'Eliminar',
            danger: true
        });
        if (!ok) return;
        try {
            await api.clientes.deleteContacto(id, c.id);
            toast.success('Contacto eliminado');
            // Si era el último de la página actual, retroceder una
            const nuevoOffset = contactos.length === 1 && offset > 0
                ? Math.max(0, offset - PAGE_SIZE)
                : offset;
            cargarContactos(nuevoOffset);
        } catch (err) {
            toast.error('Error al eliminar contacto');
        }
    };

    if (loadingCliente) {
        return <div className="loading">Cargando cliente…</div>;
    }
    if (!cliente) {
        return (
            <div className="card">
                <div className="card-body">
                    <p>Cliente no encontrado.</p>
                    <Button variant="ghost" onClick={() => navigate('/clientes')} leftIcon={<ArrowLeft size={14} />}>Volver</Button>
                </div>
            </div>
        );
    }

    const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const paginaActual = Math.floor(offset / PAGE_SIZE) + 1;

    return (
        <div className="flex flex-col gap-4">
            {/* Cabecera con volver */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/clientes')} leftIcon={<ArrowLeft size={14} />}>
                        Volver
                    </Button>
                    <div>
                        <h2 style={{ margin: 0 }}>{cliente.nombre}</h2>
                        {cliente.empresa && <span className="text-sm text-muted">{cliente.empresa}</span>}
                    </div>
                </div>
                <Button
                    variant="soft"
                    size="sm"
                    leftIcon={<FileText size={14} />}
                    onClick={() => navigate(`/cotizaciones/cliente/${id}`)}
                >
                    Ver cotizaciones
                </Button>
            </div>

            {/* Datos del cliente */}
            <Card>
                <Card.Header title="Datos del cliente" />
                <Card.Body>
                    <div className="mb-3">
                        <ClienteTipoToggle
                            value={clienteForm.tipo}
                            onChange={(tipo) => setClienteCampo('tipo', tipo)}
                            name="cliente-tipo-detalle"
                            disabled={!puedeEditar}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Input
                            id="cli-nombre"
                            label="Nombre"
                            required
                            type="text"
                            value={clienteForm.nombre}
                            onChange={(e) => setClienteCampo('nombre', e.target.value)}
                            disabled={!puedeEditar}
                        />
                        <Input
                            id="cli-empresa"
                            label="Empresa"
                            required={!esPersona}
                            hint={esPersona ? 'En persona natural se usa el nombre como razón social.' : undefined}
                            type="text"
                            value={empresaEfectiva(clienteForm)}
                            onChange={(e) => setClienteCampo('empresa', e.target.value)}
                            disabled={!puedeEditar || esPersona}
                        />
                        <Input
                            id="cli-email"
                            label="Email"
                            type="email"
                            value={clienteForm.email}
                            onChange={(e) => setClienteForm(prev => ({ ...prev, email: e.target.value }))}
                            disabled={!puedeEditar}
                        />
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="cli-telefono" className="text-xs font-medium text-muted">Teléfono</label>
                            <PhoneInput
                                id="cli-telefono"
                                value={clienteForm.telefono}
                                onChange={(v) => setClienteForm(prev => ({ ...prev, telefono: v || '' }))}
                                disabled={!puedeEditar}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5 mt-4">
                        <label htmlFor="cli-notas" className="text-xs font-medium text-muted">Notas del cliente</label>
                        <textarea
                            id="cli-notas"
                            className="bg-surface border border-line rounded-lg px-3 py-2 text-sm text-text outline-none focus:ring-2 focus:ring-cyan2/30 focus:border-cyan2 placeholder:text-muted/70 transition"
                            rows={3}
                            value={clienteForm.notas}
                            onChange={(e) => setClienteForm(prev => ({ ...prev, notas: e.target.value }))}
                            disabled={!puedeEditar}
                            placeholder="Información interna sobre el cliente"
                        />
                    </div>

                    {puedeEditar && (
                        <div className="flex justify-end gap-2 mt-4">
                            <Button variant="primary" onClick={guardarCliente} disabled={savingCliente}>
                                {savingCliente ? 'Guardando…' : 'Guardar cambios'}
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Contactos */}
            <Card>
                <Card.Header
                    title={`Contactos${total > 0 ? ` (${total})` : ''}`}
                    actions={puedeEditar && (
                        <Button
                            variant="primary"
                            size="sm"
                            leftIcon={<Plus size={14} />}
                            onClick={abrirNuevoContacto}
                        >
                            Agregar contacto
                        </Button>
                    )}
                />
                <Card.Body>
                    {/* Buscador */}
                    <div className="mb-4">
                        <Input
                            id="cli-buscar-contacto"
                            type="search"
                            placeholder="Buscar por nombre, email, teléfono o cargo…"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            prefix={<Search size={14} />}
                            suffix={busqueda && (
                                <button
                                    type="button"
                                    onClick={() => setBusqueda('')}
                                    className="text-muted hover:text-text transition"
                                    aria-label="Limpiar búsqueda"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        />
                    </div>

                    {/* Tabla */}
                    {loadingContactos ? (
                        <div className="loading">Cargando contactos…</div>
                    ) : contactos.length === 0 ? (
                        <div className="text-center text-muted py-8 text-sm">
                            {busquedaDebounced ? 'Sin resultados para esa búsqueda.' : 'Este cliente no tiene contactos registrados.'}
                        </div>
                    ) : (
                        <>
                            <div className="table-scroll">
                                <table className="galTable">
                                    <thead>
                                        <tr>
                                            <th>Nombre</th>
                                            <th>Cargo</th>
                                            <th>Email</th>
                                            <th>Teléfono</th>
                                            {puedeEditar && <th style={{ width: 90, textAlign: 'right' }}>Acciones</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contactos.map(c => (
                                            <tr key={c.id}>
                                                <td><strong>{c.nombre}</strong></td>
                                                <td>{c.cargo || <span className="text-muted">—</span>}</td>
                                                <td>{c.email || <span className="text-muted">—</span>}</td>
                                                <td>{c.telefono || <span className="text-muted">—</span>}</td>
                                                {puedeEditar && (
                                                    <td style={{ textAlign: 'right' }}>
                                                        <div className="flex justify-end gap-1">
                                                            <Tooltip content="Editar contacto">
                                                                <button
                                                                    className="btn-icon edit"
                                                                    onClick={() => abrirEditarContacto(c)}
                                                                    aria-label="Editar"
                                                                >
                                                                    <Pencil size={16} />
                                                                </button>
                                                            </Tooltip>
                                                            <Tooltip content="Eliminar contacto">
                                                                <button
                                                                    className="btn-icon delete"
                                                                    onClick={() => eliminarContacto(c)}
                                                                    aria-label="Eliminar"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </Tooltip>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Paginación */}
                            {totalPaginas > 1 && (
                                <div className="flex items-center justify-between mt-4 text-sm text-muted">
                                    <span>
                                        Página {paginaActual} de {totalPaginas} ({total} contactos)
                                    </span>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            disabled={offset === 0}
                                            onClick={() => cargarContactos(Math.max(0, offset - PAGE_SIZE))}
                                        >
                                            ‹ Anterior
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            disabled={offset + PAGE_SIZE >= total}
                                            onClick={() => cargarContactos(offset + PAGE_SIZE)}
                                        >
                                            Siguiente ›
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modal crear/editar contacto */}
            <Modal
                open={!!contactoModal}
                onClose={savingContacto ? undefined : cerrarContactoModal}
                title={contactoModal?.id ? 'Editar contacto' : 'Nuevo contacto'}
            >
                {contactoModal && (
                    <div className="flex flex-col gap-3">
                        <Input
                            id="ct-nombre"
                            label="Nombre"
                            required
                            type="text"
                            value={contactoModal.nombre}
                            onChange={(e) => setContactoModal(prev => ({ ...prev, nombre: e.target.value }))}
                            autoFocus
                        />
                        <Input
                            id="ct-cargo"
                            label="Cargo"
                            type="text"
                            value={contactoModal.cargo || ''}
                            onChange={(e) => setContactoModal(prev => ({ ...prev, cargo: e.target.value }))}
                            placeholder="Ej: Gerente comercial"
                        />
                        <Input
                            id="ct-email"
                            label="Email"
                            type="email"
                            value={contactoModal.email || ''}
                            onChange={(e) => setContactoModal(prev => ({ ...prev, email: e.target.value }))}
                        />
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="ct-telefono" className="text-xs font-medium text-muted">Teléfono</label>
                            <PhoneInput
                                id="ct-telefono"
                                value={contactoModal.telefono || ''}
                                onChange={(v) => setContactoModal(prev => ({ ...prev, telefono: v || '' }))}
                            />
                        </div>
                        <div className="flex justify-end gap-2 mt-2">
                            <Button variant="ghost" onClick={cerrarContactoModal} disabled={savingContacto}>
                                Cancelar
                            </Button>
                            <Button variant="primary" onClick={guardarContacto} disabled={savingContacto}>
                                {savingContacto ? 'Guardando…' : contactoModal.id ? 'Guardar' : 'Crear'}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ClienteDetalle;
